import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { message } from 'antd';
import MarketingLayout from '../MarketingLayout';
import request from '../../../utils/request';
import SafeVideoEmbed from '../../../components/SafeVideoEmbed';
import { getPublicResourceItemDetail, ResourceHubNav } from '../../../services/content';
import { ContentItem } from '../../../types/content';
import { routeSlugToChannelKey, RESOURCE_CHANNELS } from '../../../config/resourceHub';
import { sanitizeAndNormalizeRichText } from '../../../utils/richText';
import { useDocumentMeta } from '../../../hooks/useDocumentMeta';

const truncateNavTitle = (title: string, maxLen = 15) => {
  const text = String(title || '');
  return text.length > maxLen ? `${text.slice(0, maxLen)}...` : text;
};

const formatFileSize = (bytes?: number | null): string => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const ResourceItemDetailPage: React.FC = () => {
  const { channelSlug, slug } = useParams<{ channelSlug: string; slug: string }>();
  const channelKey = routeSlugToChannelKey(channelSlug || '');
  const channelConfig = RESOURCE_CHANNELS.find((c) => c.routeSlug === channelSlug);

  const [item, setItem] = useState<ContentItem | null>(null);
  const [prevNav, setPrevNav] = useState<ResourceHubNav | null>(null);
  const [nextNav, setNextNav] = useState<ResourceHubNav | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);
  // 默认优先展示 B站（国内客户访问更稳定），两个平台都有时才显示切换按钮
  const [activePlatform, setActivePlatform] = useState<'bilibili' | 'youtube'>('bilibili');

  useEffect(() => {
    const load = async () => {
      if (!channelKey || !slug) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setNotFound(false);
        const data = await getPublicResourceItemDetail(channelKey, slug);
        setItem(data.item || null);
        setActivePlatform(data.item?.video_url_bilibili ? 'bilibili' : 'youtube');
        setPrevNav(data.prev || null);
        setNextNav(data.next || null);
      } catch {
        setItem(null);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [channelKey, slug]);

  useDocumentMeta({
    title: item ? (item.seo_title || item.title) : channelConfig?.label,
    description: item ? (item.seo_description || item.summary) : undefined,
    ogImage: item?.image_url,
  });

  // 附件和图片/视频不一样，是给客户下载留档的，不是给浏览器打开看的——直接用 <a href>
  // 打开的话，浏览器碰到 PDF/图片这类自己能渲染的类型，默认行为是在新标签页里直接显示
  // 内容而不是下载，`download` 属性又只在同源时才可靠（附件文件是从 api 子域名发出的，
  // 跟页面本身不同源）。所以这里跟本项目里其它"下载"按钮一样，先把文件内容拉成
  // blob，再从这个同源的 blob 地址触发下载，不管浏览器本来想不想直接打开都会强制下载。
  const handleDownloadAttachment = async (url: string, fileName: string) => {
    if (!url) return;
    try {
      setDownloadingUrl(url);
      const resp = await request.get(url, { responseType: 'blob' as any });
      const blob = (resp as any)?.data as Blob;
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = fileName || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(href), 2000);
    } catch {
      message.error('下载失败，请重试');
    } finally {
      setDownloadingUrl(null);
    }
  };

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-4xl px-6 py-14">
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <Link to="/resources" className="hover:text-blue-600 hover:underline">
            资源中心
          </Link>
          <span>/</span>
          <Link to={`/resources/${channelSlug}`} className="hover:text-blue-600 hover:underline">
            {channelConfig?.label || '板块'}
          </Link>
        </div>

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-gray-600 shadow-sm">加载中...</div>
        ) : item && !notFound ? (
          <article className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {item.content_type && item.content_type !== 'article' ? (
                <span className="rounded bg-[#eef6ff] px-2 py-0.5 text-xs font-medium text-[#2f82e8]">
                  {item.content_type === 'video' ? '视频' : '图集'}
                </span>
              ) : null}
              {item.is_original === false ? (
                <span className="rounded bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-600">转载</span>
              ) : null}
            </div>
            <h1 className="text-3xl font-bold text-gray-900">{item.title}</h1>
            <p className="mt-2 text-sm text-gray-500">
              {(item.published_at || item.created_at || '').replace('T', ' ').slice(0, 19)}
            </p>

            {item.video_url_youtube || item.video_url_bilibili ? (
              <div className="mt-6 max-w-2xl">
                {item.video_url_youtube && item.video_url_bilibili ? (
                  <div className="mb-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setActivePlatform('bilibili')}
                      className={`rounded px-3 py-1 text-sm font-medium ${
                        activePlatform === 'bilibili' ? 'bg-[#2f82e8] text-white' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      B站观看
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivePlatform('youtube')}
                      className={`rounded px-3 py-1 text-sm font-medium ${
                        activePlatform === 'youtube' ? 'bg-[#2f82e8] text-white' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      Watch on YouTube
                    </button>
                  </div>
                ) : null}
                <SafeVideoEmbed
                  videoUrl={activePlatform === 'bilibili' ? item.video_url_bilibili || item.video_url_youtube : item.video_url_youtube || item.video_url_bilibili}
                  title={item.title}
                />
              </div>
            ) : item.image_url ? (
              <img
                src={item.image_url}
                alt={item.title}
                className="mt-6 max-h-[400px] w-full rounded-lg object-cover"
              />
            ) : null}

            {item.summary ? <p className="mt-6 text-base leading-8 text-gray-700">{item.summary}</p> : null}
            <div
              className="prose content-rich-text mt-8 max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeAndNormalizeRichText(item.body || '') }}
            />

            {item.attachments && item.attachments.length ? (
              <div className="mt-8 space-y-2 rounded-lg border border-[#d9eaff] bg-[#f7fbff] p-4">
                {item.attachments.map((att, idx) => {
                  const isDownloading = downloadingUrl === att.url;
                  return (
                    <div key={`${att.url}-${idx}`}>
                      <button
                        type="button"
                        onClick={() => handleDownloadAttachment(att.url, att.file_name)}
                        disabled={isDownloading}
                        className="text-sm font-medium text-[#2f82e8] hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isDownloading ? '正在准备下载...' : `下载附件：${att.file_name || '点击下载'}`}
                      </button>
                      {formatFileSize(att.file_size) ? (
                        <span className="ml-2 text-xs text-gray-400">{formatFileSize(att.file_size)}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}

            {item.is_original === false && (item.source_name || item.source_url) ? (
              <p className="mt-6 text-sm text-gray-500">
                来源：
                {item.source_url ? (
                  <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {item.source_name || item.source_url}
                  </a>
                ) : (
                  item.source_name
                )}
              </p>
            ) : null}

            <div className="mt-8 flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
              {prevNav ? (
                <Link
                  to={`/resources/${channelSlug}/${prevNav.slug}`}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  title={prevNav.title}
                >
                  上一篇：{truncateNavTitle(prevNav.title)}
                </Link>
              ) : (
                <span className="cursor-not-allowed text-sm text-gray-400">上一篇</span>
              )}
              {nextNav ? (
                <Link
                  to={`/resources/${channelSlug}/${nextNav.slug}`}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  title={nextNav.title}
                >
                  下一篇：{truncateNavTitle(nextNav.title)}
                </Link>
              ) : (
                <span className="cursor-not-allowed text-sm text-gray-400">下一篇</span>
              )}
            </div>
          </article>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-gray-500 shadow-sm">内容不存在或已下线</div>
        )}
      </section>
    </MarketingLayout>
  );
};

export default ResourceItemDetailPage;
